import { Flex, Input } from '@chakra-ui/react'

import { ActionButton } from '../../../../components/ActionButton'
import { FormField } from '../../../../components/FormField'
import { RichTextField } from '../../../../components/RichTextField'
import { SectionCard } from '../../../../components/SectionCard'
import { ICourseModuleFields } from '../../../../services/CoursesService/interfaces'

export const baseModule = (): ICourseModuleFields => ({
  _id: crypto.randomUUID(),
  title: '',
  content: ''
})

/** Mesma regra da API: o título é obrigatório; o conteúdo pode ficar vazio. */
export const validateModules = (modules: ICourseModuleFields[]): Record<string, string[]> => {
  const errors: Record<string, string[]> = {}
  modules.forEach((module, index) => {
    if (module.title.trim() === '') {
      errors[`modules.${index}.title`] = ['O título do módulo é obrigatório.']
    }
  })
  return errors
}

interface IProps {
  modules: ICourseModuleFields[]
  onChange: (next: ICourseModuleFields[]) => void
  disabled?: boolean
  /** Erros por caminho com ponto (`modules.0.title`), como a API devolve. */
  fieldErrors?: Record<string, string[]>
}

/**
 * Módulos da página do curso, presencial (ABRA-406) ou gravado (ABRA-437): título e conteúdo, na
 * ordem em que o site mostra. Um módulo sem conteúdo aparece no site sem o botão de abrir.
 */
export function CourseModulesEditor({ modules, onChange, disabled, fieldErrors }: IProps) {
  const add = () => onChange([...modules, baseModule()])

  const update = (id: string, field: 'title' | 'content', value: string) =>
    onChange(modules.map((m) => (m._id === id ? { ...m, [field]: value } : m)))

  const remove = (id: string) => onChange(modules.filter((m) => m._id !== id))

  const move = (index: number, direction: -1 | 1) => {
    const target = index + direction
    if (target < 0 || target >= modules.length) return
    const next = [...modules]
    ;[next[index], next[target]] = [next[target], next[index]]
    onChange(next)
  }

  const errorFor = (index: number, field: 'title' | 'content') => fieldErrors?.[`modules.${index}.${field}`]?.[0]

  return (
    <>
      {modules.map((module, index) => (
        <SectionCard
          key={module._id}
          variant="tile"
          title={`Módulo ${index + 1}`}
          titleAs="h3"
          actions={
            <Flex align="center" gap={1}>
              <ActionButton
                action="move-up"
                label="Mover módulo para cima"
                mr={0}
                onClick={() => move(index, -1)}
                isDisabled={disabled || index === 0}
              />
              <ActionButton
                action="move-down"
                label="Mover módulo para baixo"
                mr={0}
                onClick={() => move(index, 1)}
                isDisabled={disabled || index === modules.length - 1}
              />
              <ActionButton
                action="delete"
                label="Remover módulo"
                variant="ghost"
                mr={0}
                onClick={() => remove(module._id)}
                isDisabled={disabled}
              />
            </Flex>
          }
          mt={3}
        >
          <Flex direction="column" gap={3}>
            <FormField label="Título" error={errorFor(index, 'title')} isRequired isDisabled={disabled}>
              <Input
                size="sm"
                value={module.title}
                onChange={(e) => update(module._id, 'title', e.target.value)}
                maxLength={255}
                placeholder="Ex.: Módulo 1 - Introdução ao Photoshop"
              />
            </FormField>

            <RichTextField
              label="Conteúdo"
              error={errorFor(index, 'content')}
              value={module.content}
              onChange={(content) => update(module._id, 'content', content)}
              placeholder="Tópicos do módulo..."
              isReadOnly={disabled}
            />
          </Flex>
        </SectionCard>
      ))}

      <ActionButton
        action="create"
        label="Adicionar módulo"
        showLabel
        hideTooltip
        onClick={add}
        isDisabled={disabled}
        mt={3}
      />
    </>
  )
}
